import { useMemo, useState, type FC } from "react";
import { LeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";

import { DataTable, Input, SegmentedControl, StatusBadge } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { useComplianceResult } from "../../../queries/regulator-portal/compliance/useComplianceResult";
import { formatUtcTimestamp } from "../../../queries/regulator-portal/compliance/formatUtcTimestamp";
import {
  complianceCountTone,
  filterFindingsByCompliance,
  filterFindingsByServiceName,
} from "../../../queries/regulator-portal/compliance/parseComplianceResult";
import type {
  ComplianceResultFinding,
  ComplianceRunningImage,
  ComplianceTone,
  FindingComplianceFilter,
} from "../../../queries/regulator-portal/compliance/types";
import {
  decodeRegulatorPortalPathId,
  REGULATOR_PORTAL_RESULTS_ROUTE,
} from "../regulatorPortalRoutes";
import ComplianceStatusBadge from "./ComplianceStatusBadge";
import { portalComplianceErrorMessage } from "./portalComplianceError";

import styles from "./Compliance.module.css";

const COUNT_TONE: Record<ComplianceTone, ComplianceTone> = {
  success: "success",
  danger: "danger",
  warning: "warning",
  info: "info",
  neutral: "neutral",
};

const runningImageLabel = (
  image: ComplianceRunningImage,
  index: number,
): string =>
  image.componentName ||
  image.repository ||
  formatString(AppStrings.PAM_REGULATOR_PORTAL_IMAGE_FALLBACK, {
    index: String(index + 1),
  });

const FindingDetails: FC<{ finding: ComplianceResultFinding }> = ({
  finding,
}) => {
  if (finding.runningImages.length === 0) {
    return <>{finding.details || AppStrings.EMPTY_REASON}</>;
  }
  return (
    <div className={styles.findingDetails}>
      <ul className={styles.runningImages}>
        {finding.runningImages.map((image, index) => {
          const label = runningImageLabel(image, index);
          const showLabel = Boolean(label) && label !== finding.service;
          return (
            <li
              key={`${image.repository}-${image.componentName}-${index}`}
              className={styles.runningImage}
            >
              {showLabel ? (
                <div className={styles.runningImageTitle}>{label}</div>
              ) : null}
              {image.status || image.complianceCheck ? (
                <div className={styles.runningImageMeta}>
                  {image.status ? (
                    <ComplianceStatusBadge value={image.status} />
                  ) : null}
                  {image.complianceCheck ? (
                    <ComplianceStatusBadge value={image.complianceCheck} />
                  ) : null}
                </div>
              ) : null}
              {image.tags.length > 0 ? (
                <ul className={styles.imageRefs}>
                  {image.tags.map((tag) => (
                    <li key={`tag-${tag}`}>
                      <span className={styles.imageRefLabel}>
                        {AppStrings.PAM_REGULATOR_PORTAL_IMAGE_TAG}
                      </span>{" "}
                      {tag}
                    </li>
                  ))}
                </ul>
              ) : null}
              {image.deployedHashes.length > 0 ? (
                <ul className={styles.imageRefs}>
                  {image.deployedHashes.map((hash) => (
                    <li key={`hash-${hash}`}>
                      <span className={styles.imageRefLabel}>
                        {AppStrings.PAM_REGULATOR_PORTAL_DEPLOYED_HASH}
                      </span>{" "}
                      {hash}
                    </li>
                  ))}
                </ul>
              ) : null}
              {image.lastScannedAt ? (
                <div className={styles.runningImageScanned}>
                  {formatString(AppStrings.PAM_REGULATOR_PORTAL_LAST_SCANNED, {
                    timestamp: formatUtcTimestamp(image.lastScannedAt),
                  })}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CountChip: FC<{
  label: string;
  value: number | null | undefined;
  tone: ComplianceTone;
}> = ({ label, value, tone }) => (
  <StatusBadge tone={COUNT_TONE[tone]} size="sm">
    {label}: {value}
  </StatusBadge>
);

type CountField = {
  key: "total" | "success" | "failed" | "completed" | "compliant" | "non_compliant";
  label: string;
};

const OUTCOME_COUNT_FIELDS: CountField[] = [
  { key: "total", label: AppStrings.TOTAL },
  { key: "success", label: AppStrings.PAM_REGULATOR_PORTAL_SUCCESS_COUNT },
  { key: "failed", label: AppStrings.FAILED },
  { key: "completed", label: AppStrings.COMPLETED },
];

const COMPLIANCE_COUNT_FIELDS: CountField[] = [
  { key: "compliant", label: AppStrings.PAM_REGULATOR_PORTAL_COMPLIANT },
  { key: "non_compliant", label: AppStrings.PAM_REGULATOR_PORTAL_NON_COMPLIANT },
];

const emptyFindingsDescription = (
  filter: FindingComplianceFilter,
  serviceSearch: string,
): string => {
  if (serviceSearch.trim()) {
    return AppStrings.PAM_REGULATOR_PORTAL_FINDINGS_SEARCH_EMPTY;
  }
  if (filter === "compliant") {
    return AppStrings.PAM_REGULATOR_PORTAL_FINDINGS_COMPLIANT_EMPTY;
  }
  if (filter === "non_compliant") {
    return AppStrings.PAM_REGULATOR_PORTAL_FINDINGS_NON_COMPLIANT_EMPTY;
  }
  return AppStrings.PAM_REGULATOR_PORTAL_FINDINGS_EMPTY;
};

const RegulatorPortalResultDetail: FC = () => {
  const navigate = useNavigate();
  const { resultId = "" } = useParams<{ resultId: string }>();
  const decodedId = decodeRegulatorPortalPathId(resultId);
  const [complianceFilter, setComplianceFilter] =
    useState<FindingComplianceFilter>("all");
  const [serviceSearch, setServiceSearch] = useState("");
  const detailQuery = useComplianceResult(decodedId || undefined);
  const detail = detailQuery.data ?? null;
  const loadError =
    detailQuery.isError && !detail
      ? portalComplianceErrorMessage(
          detailQuery.error,
          AppStrings.PAM_REGULATOR_PORTAL_RESULT_LOAD_ERROR,
        )
      : null;
  const counts = detail
    ? {
        total: detail.total,
        success: detail.success,
        failed: detail.failed,
        compliant: detail.compliant,
        non_compliant: detail.non_compliant,
        completed: detail.completed,
      }
    : null;
  const outcomeCounts = OUTCOME_COUNT_FIELDS.filter(
    (field) => counts?.[field.key] != null,
  );
  const complianceCounts = COMPLIANCE_COUNT_FIELDS.filter(
    (field) => counts?.[field.key] != null,
  );
  const visibleFindings = useMemo(
    () =>
      filterFindingsByServiceName(
        filterFindingsByCompliance(detail?.findings ?? [], complianceFilter),
        serviceSearch,
      ),
    [detail?.findings, complianceFilter, serviceSearch],
  );

  const findingColumns = useMemo(
    () => [
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_TARGET,
        dataIndex: "name",
        key: "name",
        render: (value: string) => value || AppStrings.EMPTY_REASON,
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_DETAILS,
        key: "details",
        ellipsis: false,
        render: (_value: string, finding: ComplianceResultFinding) => (
          <FindingDetails finding={finding} />
        ),
      },
    ],
    [],
  );

  return (
    <section className={styles.page} data-testid="regulator-portal-result-detail">
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          aria-label={AppStrings.PAM_REGULATOR_PORTAL_BACK_TO_RESULTS}
          onClick={() => navigate(REGULATOR_PORTAL_RESULTS_ROUTE)}
        >
          <LeftOutlined className={styles.backButtonIcon} />
        </button>
        <h1 className={styles.title}>
          {AppStrings.PAM_REGULATOR_PORTAL_RESULT_DETAIL}
        </h1>
        <div className={styles.headerMeta}>
          {decodedId || detail?.id ? (
            <span className={styles.headerId} data-testid="regulator-portal-result-id">
              {detail?.id || decodedId}
            </span>
          ) : null}
          {detail?.status ? (
            <ComplianceStatusBadge
              value={detail.status}
              data-testid="regulator-portal-result-status"
            />
          ) : null}
          {detail?.client ? (
            <StatusBadge tone="neutral" size="sm" data-testid="regulator-portal-result-client">
              {AppStrings.PAM_REGULATOR_PORTAL_CLIENT}: {detail.client}
            </StatusBadge>
          ) : null}
          {detail?.regulator ? (
            <StatusBadge
              tone="neutral"
              size="sm"
              data-testid="regulator-portal-result-regulator"
            >
              {AppStrings.PAM_REGULATOR_PORTAL_REGULATOR_NAME}: {detail.regulator}
            </StatusBadge>
          ) : null}
          {detail?.trigger ? (
            <StatusBadge tone="neutral" size="sm" data-testid="regulator-portal-result-trigger">
              {AppStrings.PAM_REGULATOR_PORTAL_TRIGGER}: {detail.trigger}
            </StatusBadge>
          ) : null}
        </div>
        {detail ? (
          <div className={styles.headerFilter}>
            <SegmentedControl<FindingComplianceFilter>
              ariaLabel={AppStrings.PAM_REGULATOR_PORTAL_FILTER_FINDINGS}
              data-testid="regulator-portal-result-filter"
              value={complianceFilter}
              onChange={setComplianceFilter}
              items={[
                { id: "all", label: AppStrings.PAM_REGULATOR_PORTAL_FILTER_ALL },
                { id: "compliant", label: AppStrings.PAM_REGULATOR_PORTAL_COMPLIANT },
                {
                  id: "non_compliant",
                  label: AppStrings.PAM_REGULATOR_PORTAL_NON_COMPLIANT,
                },
              ]}
            />
          </div>
        ) : null}
      </div>

      {detailQuery.isLoading ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_LOADING}</p>
      ) : null}
      {loadError ? (
        <p className={styles.message} role="alert">
          {loadError}
        </p>
      ) : null}
      {!detailQuery.isLoading && !loadError && !detail ? (
        <p className={styles.message}>
          {AppStrings.PAM_REGULATOR_PORTAL_RESULT_NOT_FOUND}
        </p>
      ) : null}

      {!detailQuery.isLoading && detail ? (
        <>
          {detail.status.toUpperCase() === "RUNNING" ? (
            <p className={styles.runningNotice} data-testid="regulator-portal-result-running">
              {AppStrings.PAM_REGULATOR_PORTAL_RESULT_RUNNING}
            </p>
          ) : null}

          {outcomeCounts.length > 0 || complianceCounts.length > 0 ? (
            <div className={styles.countsStack} data-testid="regulator-portal-result-counts">
              {outcomeCounts.length > 0 ? (
                <div
                  className={styles.countsRow}
                  data-testid="regulator-portal-result-outcome-counts"
                >
                  {outcomeCounts.map((field) => (
                    <CountChip
                      key={field.key}
                      label={field.label}
                      value={counts?.[field.key]}
                      tone={complianceCountTone(field.key)}
                    />
                  ))}
                </div>
              ) : null}
              {complianceCounts.length > 0 ? (
                <div
                  className={styles.countsRow}
                  data-testid="regulator-portal-result-compliance-counts"
                >
                  {complianceCounts.map((field) => (
                    <CountChip
                      key={field.key}
                      label={field.label}
                      value={counts?.[field.key]}
                      tone={complianceCountTone(field.key)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className={styles.detailSection}>
            <div className={styles.detailSectionHeader}>
              <h2 className={styles.detailSectionTitle}>
                {AppStrings.PAM_REGULATOR_PORTAL_CHECKS}
              </h2>
              <Input
                variant="search"
                className={styles.serviceSearch}
                placeholder={AppStrings.PAM_REGULATOR_PORTAL_SEARCH_SERVICES}
                value={serviceSearch}
                onChange={(event) => setServiceSearch(event.target.value)}
                allowClear
                data-testid="regulator-portal-result-search"
              />
            </div>
            {visibleFindings.length > 0 ? (
              <DataTable<ComplianceResultFinding>
                rowKey="id"
                columns={findingColumns}
                dataSource={visibleFindings}
                pagination={false}
                scrollPreset="wide"
                data-testid="regulator-portal-result-findings"
              />
            ) : (
              <p className={styles.message}>
                {emptyFindingsDescription(complianceFilter, serviceSearch)}
              </p>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
};

export default RegulatorPortalResultDetail;
